import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapPin, Plus, Pencil, Trash2, Star, Package, LogOut, User, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  apiGetAddresses, apiAddAddress, apiUpdateAddress,
  apiDeleteAddress, apiSetDefaultAddress, apiGetMyOrders
} from "../api";

const STATUS_COLOR = {
  Pending: "bg-yellow-100 text-yellow-800",
  Confirmed: "bg-blue-100 text-blue-800",
  Shipped: "bg-purple-100 text-purple-800",
  Delivered: "bg-green-100 text-green-800",
  Cancelled: "bg-red-100 text-red-800",
};

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh",
  "Uttarakhand","West Bengal","Delhi","Jammu & Kashmir","Ladakh","Puducherry",
];

const emptyAddr = { label: "Home", name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", isDefault: false };

const inputCls = "w-full border border-neutral-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8932F]";

const AddressForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || emptyAddr);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[#f6f3ee] rounded-sm p-5 space-y-4 border border-neutral-200">
      <div className="flex gap-3">
        {["Home", "Work", "Other"].map((l) => (
          <button type="button" key={l} onClick={() => set("label", l)}
            className={`px-4 py-1.5 text-xs rounded-full border font-medium transition-all ${
              form.label === l
                ? "bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] border-transparent"
                : "border-neutral-300 text-neutral-600 hover:border-[#C8932F]"
            }`}>
            {l}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[["name","Full Name","text",true],["phone","Phone Number","tel",true]].map(([k,p,t,req]) => (
          <div key={k}>
            <label className="text-xs text-neutral-500 mb-1 block capitalize">{p}</label>
            <input required={req} type={t} placeholder={p} value={form[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} />
          </div>
        ))}
      </div>
      <div>
        <label className="text-xs text-neutral-500 mb-1 block">Address Line 1</label>
        <input required placeholder="House No., Street, Area" value={form.line1} onChange={(e) => set("line1", e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className="text-xs text-neutral-500 mb-1 block">Address Line 2 (optional)</label>
        <input placeholder="Landmark, Near..." value={form.line2} onChange={(e) => set("line2", e.target.value)} className={inputCls} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">City</label>
          <input required placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">State</label>
          <select required value={form.state} onChange={(e) => set("state", e.target.value)}
            className={inputCls + " bg-white"}>
            <option value="">Select State</option>
            {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">Pincode</label>
          <input required placeholder="6-digit pincode" value={form.pincode} onChange={(e) => set("pincode", e.target.value)}
            maxLength={6} className={inputCls} />
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.isDefault} onChange={(e) => set("isDefault", e.target.checked)} className="accent-[#C8932F]" />
        <span className="text-sm text-neutral-600">Set as default address</span>
      </label>
      <div className="flex gap-3 pt-1">
        <button type="submit" disabled={saving}
          className="bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] px-6 py-2.5 text-sm font-semibold rounded-sm hover:opacity-90 transition-opacity disabled:opacity-60">
          {saving ? "Saving..." : "Save Address"}
        </button>
        <button type="button" onClick={onCancel} className="px-6 py-2.5 text-sm border border-neutral-300 rounded-sm text-neutral-600 hover:border-neutral-400">
          Cancel
        </button>
      </div>
    </form>
  );
};

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [addresses, setAddresses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingAddr, setLoadingAddr] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editAddr, setEditAddr] = useState(null);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    apiGetMyOrders().then(setOrders).catch(() => setOrders([])).finally(() => setLoadingOrders(false));
    apiGetAddresses().then(setAddresses).catch(() => setAddresses([])).finally(() => setLoadingAddr(false));
  }, [user, navigate]);

  const handleAddAddress = async (form) => {
    const addr = await apiAddAddress(form);
    setAddresses((prev) => {
      const updated = form.isDefault ? prev.map((a) => ({ ...a, isDefault: false })) : [...prev];
      return [...updated, addr];
    });
    setShowForm(false);
  };

  const handleEditAddress = async (form) => {
    await apiUpdateAddress(editAddr.id, form);
    setAddresses((prev) => prev.map((a) => {
      if (form.isDefault) a = { ...a, isDefault: false };
      return a.id === editAddr.id ? { ...a, ...form } : a;
    }));
    setEditAddr(null);
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await apiDeleteAddress(id);
    setAddresses((prev) => {
      const updated = prev.filter((a) => a.id !== id);
      if (updated.length && !updated.some((a) => a.isDefault)) updated[0].isDefault = true;
      return updated;
    });
  };

  const handleSetDefault = async (id) => {
    await apiSetDefaultAddress(id);
    setAddresses((prev) => prev.map((a) => ({ ...a, isDefault: a.id === id })));
  };

  if (!user) return null;

  const tabs = [
    { key: "orders", label: "My Orders", icon: Package },
    { key: "addresses", label: "Address Book", icon: MapPin },
  ];

  return (
    <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-10 min-h-[60vh]">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-neutral-200 rounded-sm p-6 text-center mb-4">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#FFE29A] to-[#C8932F] flex items-center justify-center mx-auto mb-3">
              <User className="h-8 w-8 text-[#050304]" />
            </div>
            <h2 className="font-semibold text-neutral-900">{user.name}</h2>
            <p className="text-xs text-neutral-500 mt-0.5">{user.email}</p>
            {user.phone && <p className="text-xs text-neutral-500">{user.phone}</p>}
          </div>
          <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
            {tabs.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm border-b border-neutral-100 last:border-0 transition-colors ${
                  tab === key
                    ? "bg-[#f3ede2] text-[#C8932F] font-semibold"
                    : "text-neutral-600 hover:bg-neutral-50"
                }`}>
                <Icon className="h-4 w-4" />
                {label}
                <ChevronRight className="h-4 w-4 ml-auto opacity-40" />
              </button>
            ))}
            <button onClick={() => { logout(); navigate("/"); }}
              className="w-full flex items-center gap-3 px-5 py-3.5 text-sm text-red-500 hover:bg-red-50 transition-colors">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="lg:col-span-3">
          {/* Orders Tab */}
          {tab === "orders" && (
            <div>
              <h2 className="font-serif text-2xl text-neutral-900 mb-6">My Orders</h2>
              {loadingOrders ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-neutral-100 rounded-sm animate-pulse" />)}
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-16 border border-neutral-200 rounded-sm">
                  <Package className="h-12 w-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-600">No orders yet.</p>
                  <Link to="/collections/all" className="mt-4 inline-block text-[#C8932F] text-sm hover:underline">Start Shopping</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="bg-white border border-neutral-200 rounded-sm p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                        <div>
                          <p className="font-semibold text-neutral-900">#{order.orderNumber}</p>
                          <p className="text-xs text-neutral-500 mt-0.5">
                            {new Date(order.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLOR[order.status] || "bg-neutral-100 text-neutral-700"}`}>
                            {order.status}
                          </span>
                          <span className="font-bold text-neutral-900">₹{order.total?.toLocaleString("en-IN")}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {order.items?.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-neutral-600">
                            <span className="h-1.5 w-1.5 bg-[#C8932F] rounded-full" />
                            {item.name} × {item.qty}
                          </div>
                        ))}
                        {order.items?.length > 3 && (
                          <span className="text-xs text-neutral-400">+{order.items.length - 3} more</span>
                        )}
                      </div>
                      <div className="mt-3 text-xs text-neutral-500">
                        {order.paymentMethod === "cod" ? "💵 Cash on Delivery" : "💳 Online Payment"} •{" "}
                        {order.shipping === 0 ? "Free Shipping" : `₹${order.shipping} Shipping`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Address Book Tab */}
          {tab === "addresses" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl text-neutral-900">Address Book</h2>
                {!showForm && !editAddr && (
                  <button onClick={() => setShowForm(true)}
                    className="flex items-center gap-2 bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] px-4 py-2.5 text-sm font-semibold rounded-sm hover:opacity-90 transition-opacity">
                    <Plus className="h-4 w-4" /> Add Address
                  </button>
                )}
              </div>

              {showForm && (
                <div className="mb-6">
                  <h3 className="font-semibold text-neutral-900 mb-3">New Address</h3>
                  <AddressForm onSave={handleAddAddress} onCancel={() => setShowForm(false)} />
                </div>
              )}

              {editAddr && (
                <div className="mb-6">
                  <h3 className="font-semibold text-neutral-900 mb-3">Edit Address</h3>
                  <AddressForm initial={editAddr} onSave={handleEditAddress} onCancel={() => setEditAddr(null)} />
                </div>
              )}

              {loadingAddr ? (
                <div className="space-y-3">
                  {[...Array(2)].map((_, i) => <div key={i} className="h-32 bg-neutral-100 rounded-sm animate-pulse" />)}
                </div>
              ) : addresses.length === 0 && !showForm ? (
                <div className="text-center py-16 border border-dashed border-neutral-300 rounded-sm">
                  <MapPin className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-500">No saved addresses yet.</p>
                  <button onClick={() => setShowForm(true)} className="mt-4 text-[#C8932F] text-sm hover:underline">Add your first address</button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {addresses.map((addr) => (
                    <div key={addr.id} className={`relative bg-white border rounded-sm p-5 ${addr.isDefault ? "border-[#C8932F]" : "border-neutral-200"}`}>
                      {addr.isDefault && (
                        <span className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-semibold text-[#C8932F] bg-[#f3ede2] px-2 py-0.5 rounded-full">
                          <Star className="h-3 w-3" /> Default
                        </span>
                      )}
                      <div className="mb-1">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-[#C8932F] bg-[#f3ede2] px-2 py-0.5 rounded-full">{addr.label}</span>
                      </div>
                      <p className="font-semibold text-neutral-900 mt-2">{addr.name}</p>
                      <p className="text-sm text-neutral-600 mt-1">{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}</p>
                      <p className="text-sm text-neutral-600">{addr.city}, {addr.state} — {addr.pincode}</p>
                      <p className="text-sm text-neutral-500 mt-0.5">{addr.phone}</p>
                      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-neutral-100">
                        <button onClick={() => { setEditAddr(addr); setShowForm(false); }}
                          className="flex items-center gap-1 text-xs text-neutral-500 hover:text-[#C8932F] transition-colors">
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                        <button onClick={() => handleDeleteAddress(addr.id)}
                          className="flex items-center gap-1 text-xs text-neutral-500 hover:text-[#D6425F] transition-colors">
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                        {!addr.isDefault && (
                          <button onClick={() => handleSetDefault(addr.id)}
                            className="flex items-center gap-1 text-xs text-neutral-500 hover:text-[#C8932F] transition-colors ml-auto">
                            Set as Default
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
